/******* Cart Inventory Tracker *******/

class CartInventoryTracker {
  constructor() {
    this.cartDataElement = null;
    this.observer = null;
    this.cartData = null;
    this.deliveryChangeHandler = null;
    this.locationSelectChangeHandler = null;
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  async setup() {
    this.cartDataElement = document.getElementById('cart-data');
    
    if (this.cartDataElement) {
      this.updateCartData();
    } else {
      await this.fetchCartData();
    }
    
    this.markOriginallyUnavailableCards();
    this.checkAllProductCards();
    this.observeCartContainer();
    this.observeDeliveryChanges();
  }

  /* Fetch cart data from Shopify API when cart-data element is not available */
  async fetchCartData() {
    try {
      const response = await fetch('/cart.js');
      this.cartData = await response.json();
    } catch (error) {
      console.error('Error fetching cart data:', error);
      this.cartData = { items: [], attributes: {} };
    }
  }

  /* Parse and update cart data from the cart-data element */
  updateCartData() {
    try {
      // Re-query the element in case it was replaced
      this.cartDataElement = document.getElementById('cart-data');
      const cartDataText = this.cartDataElement?.textContent || '{}';
      this.cartData = JSON.parse(cartDataText);
    } catch (error) {
      console.error('Error parsing cart data:', error);
      this.cartData = { items: [] };
    }
  }

  /* Listen for delivery selector changes (location changes) */
  observeDeliveryChanges() {
    this.deliveryChangeHandler = () => {
      this.handleDeliveryChange();
    };
    document.addEventListener('delivery-changed', this.deliveryChangeHandler);
    
    this.attachLocationSelectListener();
  }
  
  attachLocationSelectListener() {
    const locationSelect = document.getElementById('location');
    
    if (locationSelect) {
      this.locationSelectChangeHandler = () => {
        this.handleDeliveryChange();
      };
      locationSelect.addEventListener('change', this.locationSelectChangeHandler);
    } else {
      setTimeout(() => {
        const retrySelect = document.getElementById('location');
        if (retrySelect && !this.locationSelectChangeHandler) {
          this.locationSelectChangeHandler = () => {
            this.handleDeliveryChange();
          };
          retrySelect.addEventListener('change', this.locationSelectChangeHandler);
        }
      }, 500);
    }
  }

  /* Handle delivery selector changes by re-checking cards with new location */
  async handleDeliveryChange() {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    this.markOriginallyUnavailableCards();
    this.checkAllProductCards();
    
    setTimeout(() => {
      this.checkAllProductCards();
    }, 500);
  }

  /* Set up MutationObserver to watch for cart container changes (including #cart-data replacement) */
  observeCartContainer() {
    if (this.observer) {
      this.observer.disconnect();
    }

    const targetNode = document.body;

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          const addedNodes = Array.from(mutation.addedNodes);
          const removedNodes = Array.from(mutation.removedNodes);
          
          const hasCartData = addedNodes.some(node => 
            node.nodeType === 1 && node instanceof Element && (
              node.id === 'cart-data' || 
              node.querySelector('#cart-data')
            )
          );

          const cartDataRemoved = removedNodes.some(node => 
            node.nodeType === 1 && node instanceof Element && (
              node.id === 'cart-data' || 
              node.querySelector('#cart-data')
            )
          );

          if (hasCartData) {
            this.cartDataElement = document.getElementById('cart-data');
            if (this.cartDataElement) {
              this.updateCartData();
              this.checkAllProductCards();
            }
          } else if (cartDataRemoved) {
            this.cartDataElement = null;
            this.cartData = { items: [], attributes: {} };
            this.checkAllProductCards();
          }
          
          // Check for dynamically added product cards (e.g., in flavors-list)
          const hasNewProductCards = addedNodes.some(node => {
            if (node.nodeType === 1 && node instanceof Element) {
              return node.classList?.contains('product-card-wrapper') || node.querySelector('.product-card-wrapper');
            }
            return false;
          });
          
          if (hasNewProductCards) {
            addedNodes.forEach(node => {
              if (node.nodeType === 1 && node instanceof Element) {
                if (node.classList?.contains('product-card-wrapper') && node instanceof HTMLElement) {
                  this.markOriginallyUnavailableCard(node);
                  this.checkProductCard(node);
                } else {
                  const newCards = node.querySelectorAll('.product-card-wrapper');
                  newCards.forEach(card => {
                    if (card instanceof HTMLElement) {
                      this.markOriginallyUnavailableCard(card);
                      this.checkProductCard(card);
                    }
                  });
                }
              }
            });
          }
        }
        
        // Also check for text changes in existing cart-data
        if (mutation.type === 'characterData') {
          const target = mutation.target;
          if (target.parentElement && target.parentElement.id === 'cart-data') {
            this.updateCartData();
            this.checkAllProductCards();
          }
        }
      });
    });

    this.observer.observe(targetNode, {
      characterData: true,
      childList: true,
      subtree: true,
    });
  }

  /* Check all product cards and update their availability */
  checkAllProductCards() {
    const productCards = document.querySelectorAll('.product-card-wrapper');
    
    if (productCards.length === 0) {
      setTimeout(() => {
        const retryCards = document.querySelectorAll('.product-card-wrapper');
        if (retryCards.length > 0) {
          retryCards.forEach((card) => {
            if (card instanceof HTMLElement) {
              this.checkProductCard(card);
            }
          });
        }
      }, 300);
      return;
    }
    
    productCards.forEach((card) => {
      if (card instanceof HTMLElement) {
        this.checkProductCard(card);
      }
    });
  }

  /**
   * Check a single product card and update its availability
   * @param {HTMLElement} card - The product card wrapper element
   */
  checkProductCard(card) {
    const quantityInput = card.querySelector('.quantity-input');
    
    if (!quantityInput) {
      return;
    }

    const variantId = quantityInput instanceof HTMLElement ? quantityInput.dataset.variantId : undefined;
    
    if (!variantId) {
      return;
    }

    const isMerch = quantityInput instanceof HTMLElement && quantityInput.dataset.merch === 'true';
    const isWorkshop = quantityInput instanceof HTMLElement && quantityInput.dataset.workshop === 'true';

    if (!isWorkshop && !isMerch && !this.isTodaySelected()) {
      return;
    }

    // Get max inventory based on current cart location/type
    const maxDefault = this.getMaxInventoryForCurrentLocation(quantityInput);
    
    // If no max inventory available, skip
    if (maxDefault === null) {
      return;
    }

    // Get current quantity in cart for this variant (pass current location)
    const { type: currentType, location: currentLocation } = this.getCurrentDeliverySettings();
    const cartQuantity = this.getVariantQuantityInCart(variantId, currentType, currentLocation);
    const availableQuantity = Math.max(0, maxDefault - cartQuantity);
    const isUnavailable = availableQuantity === 0;

    // Update card availability 
    this.updateCardAvailability(card, isUnavailable);
    
    // Update max attribute on ALL quantity inputs in this card
    this.updateMaxAttributes(card, availableQuantity);
  }

  /**
   * Check if the currently selected delivery date is today
   * @returns {boolean} Whether today's date is selected
   */
  isTodaySelected() {
    const dayInput = document.querySelector('.js-delivery-date');
    let dayValue = '';
    
    if (dayInput && dayInput instanceof HTMLInputElement) {
      dayValue = dayInput.value;
    } else {
      // Fallback to localStorage if element is not present
      dayValue = localStorage.getItem('delivery-date') || '';
    }
    
    const today = new Date().toLocaleDateString('en-GB', { 
      day: '2-digit',
      month: 'long', 
      year: 'numeric' 
    });
    
    return today === dayValue;
  }

  /**
   * Get current delivery settings from the location selector
   * @returns {{type: string, location: string|null}} - Current delivery type and location
   */
  getCurrentDeliverySettings() {
    const locationSelect = document.getElementById('location');
    
    if (!locationSelect || !(locationSelect instanceof HTMLSelectElement)) {
      return {
        type: this.cartData?.attributes?.type || 'nationwide',
        location: this.cartData?.attributes?.location || null
      };
    }
    
    const selectedValue = locationSelect.value;
    
    if (selectedValue.includes('georgetown')) {
      return { type: 'local', location: 'georgetown' };
    } else if (selectedValue.includes('bethesda')) {
      return { type: 'local', location: 'bethesda' };
    }
    
    return {
      type: this.cartData?.attributes?.type || 'nationwide',
      location: this.cartData?.attributes?.location || null
    };
  }

  /**
   * Get max inventory for the current cart location/type
   * @param {HTMLElement} quantityInput - The quantity input element
   * @returns {number|null} - Max inventory or null if not available
   */
  getMaxInventoryForCurrentLocation(quantityInput) {
    const { type: cartType, location: cartLocation } = this.getCurrentDeliverySettings();
    
    let inventoryAttr;
    
    if (cartType === 'nationwide') {
      inventoryAttr = 'data-inventory-nationwide';
    } else {
      // Local delivery/pickup - use location-specific inventory
      inventoryAttr = cartLocation === 'georgetown' ? 'data-inventory-georgetown' : 'data-inventory-bethesda';
    }
    
    const inventoryValue = quantityInput.getAttribute(inventoryAttr);
    let maxInventory = inventoryValue ? parseInt(inventoryValue, 10) : null;
    
    // Fallback to max-default if location-specific inventory is not available
    // This ensures backward compatibility with existing code
    if (maxInventory === null) {
      const maxDefaultAttr = quantityInput.getAttribute('max-default');
      maxInventory = maxDefaultAttr ? parseInt(maxDefaultAttr, 10) : null;
    }
    
    return maxInventory;
  }

  /**
   * Get the total quantity of a variant in the cart, considering location-based logic
   * @param {string} variantId - The variant ID to check
   * @param {string} currentType - Current delivery type (from selector, not cart)
   * @param {string|null} currentLocation - Current location (from selector, not cart)
   * @returns {number} - Total quantity of the variant in the cart
   */
  getVariantQuantityInCart(variantId, currentType, currentLocation) {
    if (!this.cartData || !this.cartData.items) {
      return 0;
    }

    let totalQuantity = 0;

    this.cartData.items.forEach((item) => {
      if (item.variant_id?.toString() === variantId.toString()) {
        if (currentType === 'nationwide') {
          totalQuantity += item.quantity;
        } else {
          // For local delivery, only count items from the CURRENT selected location
          const itemLocation = item.properties?.deliveryLocalLocation;
          
          if (itemLocation === currentLocation) {
            totalQuantity += item.quantity;
          }
        }
      }
      
      // Check if variant is inside a bundle (custom dozen)
      if (item.properties?._flavors && item.properties?._flavorIds) {
        // Only count bundle if it matches the current location
        const shouldCountBundle = currentType === 'nationwide' || item.properties?.deliveryLocalLocation === currentLocation;
        
        if (shouldCountBundle) {
          const bundleQty = this.getVariantQuantityInBundle(
            variantId,
            item.properties._flavors,
            item.properties._flavorIds,
            item.quantity
          );

          if (bundleQty > 0) {
            totalQuantity += bundleQty;
          }
        }
      }
    });

    return totalQuantity;
  }

  /**
   * Get the quantity of a variant inside a bundle
   * @param {string} variantId - The variant ID to check
   * @param {string} flavorsString - Format: "Product A:6//Product B:1//Product C:5"
   * @param {string} flavorIdsString - Format: "41451300978821,41451302224005,41451298816133"
   * @param {number} bundleQuantity - How many bundles are in cart
   * @returns {number} - Total quantity of variant in bundles
   */
  getVariantQuantityInBundle(variantId, flavorsString, flavorIdsString, bundleQuantity) {
    try {
      const flavorIds = flavorIdsString.split(',').map(id => id.trim());
      const flavors = flavorsString.split('//').map(flavor => {
        const [name, qty] = flavor.split(':').map(part => part.trim());
        return { name, quantity: parseInt(qty, 10) || 0 };
      });

      const variantIndex = flavorIds.findIndex(id => id === variantId.toString());
      if (variantIndex === -1) return 0;

      const flavorQuantity = flavors[variantIndex]?.quantity || 0;
      return flavorQuantity * bundleQuantity;
    } catch (error) {
      console.error('Error parsing bundle flavors:', error);
      return 0;
    }
  }

  /**
   * Update product card availability state
   * @param {HTMLElement} card - The product card element
   * @param {boolean} isUnavailable - Whether the product should be marked as unavailable
   */
  updateCardAvailability(card, isUnavailable) {
    const hasUnavailableClass = card.classList.contains('is-unavailable');

    if (isUnavailable && !hasUnavailableClass) {
      card.classList.add('is-unavailable');
      this.disableAddToCart(card);
    } else if (!isUnavailable && hasUnavailableClass) {
      // Only remove if it wasn't originally unavailable (check data attribute)
      const wasOriginallyUnavailable = card.dataset.originallyUnavailable === 'true';
      if (!wasOriginallyUnavailable) {
        card.classList.remove('is-unavailable');
        this.enableAddToCart(card);
      }
    }
  }

  /**
   * Disable add to cart functionality for a product card
   * @param {HTMLElement} card - The product card element
   */
  disableAddToCart(card) {
    const addButtons = card.querySelectorAll('.quantity-plus, [name="add"], .add-to-cart-button');
    
    addButtons.forEach((button) => {
      if (button instanceof HTMLButtonElement || button instanceof HTMLInputElement) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      }
    });
  }

  /**
   * Enable add to cart functionality for a product card
   * @param {HTMLElement} card - The product card element
   */
  enableAddToCart(card) {
    const addButtons = card.querySelectorAll('.quantity-plus, [name="add"], .add-to-cart-button');
    
    addButtons.forEach((button) => {
      if (button instanceof HTMLButtonElement || button instanceof HTMLInputElement) {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      }
    });
  }

  /**
   * Update max attribute on all quantity inputs in a card
   * @param {HTMLElement} card - The product card element
   * @param {number} maxValue - The new max value
   */
  updateMaxAttributes(card, maxValue) {
    const quantityInputs = card.querySelectorAll('.quantity-input');
    
    quantityInputs.forEach((input) => {
      if (input instanceof HTMLElement) {
        input.setAttribute('max', maxValue.toString());
      }
    });
  }

  /**
   * Mark a single card as originally unavailable if needed
   * @param {HTMLElement} card - The product card element
   */
  markOriginallyUnavailableCard(card) {
    const quantityInput = card.querySelector('.quantity-input');
    if (quantityInput instanceof HTMLElement) {
      const variantId = quantityInput.dataset.variantId;
      
      if (!variantId) return;
      
      // Get max inventory for current location
      const maxDefault = this.getMaxInventoryForCurrentLocation(quantityInput);
      const hasUnavailableClass = card.classList.contains('is-unavailable');

      if (hasUnavailableClass && (maxDefault === 0 || maxDefault === null)) {
        card.dataset.originallyUnavailable = 'true';
      } else if (hasUnavailableClass && maxDefault && maxDefault > 0) {
        card.dataset.originallyUnavailable = 'false';
      }
    }
  }

  /**
   * Mark cards that were originally unavailable
   * Should be called BEFORE cart-based availability check
   */
  markOriginallyUnavailableCards() {
    const allCards = document.querySelectorAll('.product-card-wrapper');
    
    allCards.forEach((card) => {
      if (card instanceof HTMLElement) {
        this.markOriginallyUnavailableCard(card);
      }
    });
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
    }

    // Remove delivery-changed listener
    if (this.deliveryChangeHandler) {
      document.removeEventListener('delivery-changed', this.deliveryChangeHandler);
    }
    
    // Remove location select listener
    if (this.locationSelectChangeHandler) {
      const locationSelect = document.getElementById('location');
      if (locationSelect) {
        locationSelect.removeEventListener('change', this.locationSelectChangeHandler);
      }
    }
  }
}

// Initialize the tracker when the module loads
/** @type {CartInventoryTracker | null} */
let tracker = null;

export function initCartInventoryTracker() {
  if (tracker) {
    tracker.disconnect();
  }
  tracker = new CartInventoryTracker();
  return tracker;
}

if (typeof window !== 'undefined') {
  tracker = new CartInventoryTracker();
}

export default CartInventoryTracker;
